/**
 * YOJO Accessibility — cart live-region announcer
 * Wave 2 Batch 4A.2 — 2026-04-19
 *
 * Fully additive. Does NOT modify existing cart JS.
 * Writes short Hebrew status messages into the existing
 * `#screen-reader-info` live region (declared in layout/theme.liquid:566
 * with aria-live="polite") whenever cart state changes.
 *
 * Detection strategy:
 *   - Poll /cart.js (lightweight JSON) every time the drawer opens OR
 *     when the cart-icon badge mutates (which indicates an add/update/remove).
 *   - Diff previous vs current snapshot to categorise the change:
 *       item_count increased -> "נוסף לסל: <title>"
 *       item_count decreased (via drawer) -> "הוסר מהסל: <title>"
 *       item_count stayed same but a specific line's quantity changed -> "עודכנה כמות: <title>, <N>"
 *       pack normalization by yojo-cart-normalize.js -> "הסל עודכן: האריזות אוחדו ל־<packs>"
 *         (coordinated via the 'yojo:cart-normalized' document event; see below)
 *   - Do NOT announce on mere drawer open (to avoid spam).
 *
 * The announcer is passive: if the cart cannot be polled (CORS/offline) it
 * silently fails. Zero user-visible side effect.
 */
(function () {
  'use strict';

  var REGION_ID = 'screen-reader-info';

  function getRegion() {
    return document.getElementById(REGION_ID);
  }

  function speak(msg) {
    var el = getRegion();
    if (!el) return;
    // Clearing + re-setting nudges AT to re-announce even when message is identical.
    el.textContent = '';
    setTimeout(function () { el.textContent = msg; }, 30);
  }

  var lastSnapshot = null; // { item_count, lines: { [key]: { title, fullTitle, quantity } } }

  function snapshot(cart) {
    var lines = {};
    (cart.items || []).forEach(function (i) {
      lines[i.key] = {
        title: i.product_title || i.title || '',
        // i.title includes the variant ("תות שדה - 2 ק\"ג") — used for pack summaries.
        fullTitle: i.title || i.product_title || '',
        quantity: i.quantity,
      };
    });
    return { item_count: cart.item_count || 0, lines: lines };
  }

  /* --- Pack-normalization awareness — DORMANT (2026-08-25) -----------------
   * yojo-cart-normalize.js was retired 2026-08-13 (owner rule W1) and its
   * asset DELETED from the repo 2026-08-25; nothing dispatches
   * 'yojo:cart-normalized' any more, so the marker below can never arm and
   * every diff falls through to the honest add/update/remove branches. Kept
   * because the branch is interwoven with the live diff logic and inert when
   * unarmed; if a future opt-in pack-upgrade feature ships, it can re-use
   * this contract.
   *
   * Original contract: yojo-cart-normalize.js merges frozen-fruit tiers (e.g. 2×1kg -> 1×2kg)
   * with a single /cart/update.js. To the line-key diff below that looks like
   * a removal, which would announce "הוסר מהסל": false, and it implies
   * data loss. The normalizer dispatches 'yojo:cart-normalized' immediately
   * BEFORE its update request, carrying the family's variant ids and the
   * exact target plan (variant -> quantity).
   *
   * Race handling: our poll fires several times around a mutation, so we may
   * observe the PRE-normalize cart while the marker is already armed. We
   * therefore only consume the marker when the observed cart matches the
   * plan; earlier diffs fall through to the honest add/update branches. The
   * marker survives until consumed, and expires so a failed normalization
   * can never mislabel a genuine removal later.
   */
  var NORMALIZE_MARKER_TTL_MS = 15000;
  var normalizeMarker = null; // { ts, families: { [familyId]: { variantIds, plan } } }

  document.addEventListener('yojo:cart-normalized', function (e) {
    var d = e && e.detail;
    if (!d || !d.familyId || !d.variantIds || !d.variantIds.length || !d.plan) return;
    var now = Date.now();
    if (!normalizeMarker || now - normalizeMarker.ts > NORMALIZE_MARKER_TTL_MS) {
      normalizeMarker = { ts: now, families: {} };
    }
    normalizeMarker.ts = now;
    var variantIds = {};
    d.variantIds.forEach(function (vid) { variantIds[String(vid)] = true; });
    var plan = {};
    Object.keys(d.plan).forEach(function (vid) { plan[String(vid)] = d.plan[vid]; });
    normalizeMarker.families[d.familyId] = { variantIds: variantIds, plan: plan };
  });

  function activeNormalizeMarker() {
    if (normalizeMarker && Date.now() - normalizeMarker.ts > NORMALIZE_MARKER_TTL_MS) {
      normalizeMarker = null;
    }
    return normalizeMarker;
  }

  // Line keys are "<variant_id>:<hash>".
  function keyVariantId(key) {
    return String(key).split(':')[0];
  }

  // The cart matches a family's plan when, for every variant in the family,
  // the summed line quantities equal the planned quantity (0 included).
  function familyMatchesPlan(fam, curr) {
    var sums = {};
    Object.keys(curr.lines).forEach(function (k) {
      var vid = keyVariantId(k);
      if (fam.variantIds[vid]) sums[vid] = (sums[vid] || 0) + curr.lines[k].quantity;
    });
    return Object.keys(fam.variantIds).every(function (vid) {
      return (sums[vid] || 0) === (fam.plan[vid] || 0);
    });
  }

  // Returns the truthful merge announcement if this diff is a completed pack
  // normalization; null otherwise (caller falls through to normal branches).
  function normalizationMessage(prev, curr) {
    var marker = activeNormalizeMarker();
    if (!marker) return null;

    var changed = [];
    Object.keys(prev.lines).forEach(function (k) {
      if (!curr.lines[k] || curr.lines[k].quantity !== prev.lines[k].quantity) changed.push(k);
    });
    Object.keys(curr.lines).forEach(function (k) {
      if (!prev.lines[k]) changed.push(k);
    });
    if (changed.length === 0) return null;

    // Every changed line must belong to a marked family…
    var owningFamilies = {};
    var allOwned = changed.every(function (k) {
      var vid = keyVariantId(k);
      var fids = Object.keys(marker.families);
      for (var i = 0; i < fids.length; i++) {
        if (marker.families[fids[i]].variantIds[vid]) { owningFamilies[fids[i]] = true; return true; }
      }
      return false;
    });
    if (!allOwned) return null;

    // …and each touched family must have landed exactly on its target plan.
    var fidList = Object.keys(owningFamilies);
    var matched = fidList.every(function (fid) {
      return familyMatchesPlan(marker.families[fid], curr);
    });
    if (!matched) return null; // pre-normalize diff — announce honestly below

    // Consume the matched families; keep any still-pending ones armed.
    var summaryParts = [];
    Object.keys(curr.lines).forEach(function (k) {
      var vid = keyVariantId(k);
      for (var i = 0; i < fidList.length; i++) {
        if (marker.families[fidList[i]].variantIds[vid] && curr.lines[k].quantity > 0) {
          summaryParts.push(curr.lines[k].quantity + ' × ' + (curr.lines[k].fullTitle || curr.lines[k].title));
          return;
        }
      }
    });
    fidList.forEach(function (fid) { delete marker.families[fid]; });
    if (Object.keys(marker.families).length === 0) normalizeMarker = null;

    if (summaryParts.length > 0) {
      return 'הסל עודכן: האריזות אוחדו ל־' + summaryParts.join(', ') + '.';
    }
    return 'הסל עודכן: האריזות אוחדו לאריזה גדולה יותר.';
  }

  function announceDiff(prev, curr) {
    if (!prev) return; // first-ever observation; nothing to announce

    // Pack normalization is an update, not a removal — check it first so a
    // merged tier is never announced as "הוסר מהסל".
    var normalizeMsg = normalizationMessage(prev, curr);
    if (normalizeMsg) {
      speak(normalizeMsg);
      return;
    }
    var prevCount = prev.item_count;
    var currCount = curr.item_count;

    // Pure add
    if (currCount > prevCount) {
      // Find the key that appeared OR whose qty went up
      var newKeys = Object.keys(curr.lines).filter(function (k) { return !prev.lines[k]; });
      if (newKeys.length > 0) {
        var t = curr.lines[newKeys[0]].title;
        speak('נוסף לסל: ' + t + '.');
        return;
      }
      // Existing line incremented
      for (var k in curr.lines) {
        if (prev.lines[k] && curr.lines[k].quantity > prev.lines[k].quantity) {
          speak('עודכנה כמות: ' + curr.lines[k].title + ', ' + curr.lines[k].quantity + '.');
          return;
        }
      }
    }

    // Pure remove / decrement
    if (currCount < prevCount) {
      // Find the key that disappeared
      var goneKeys = Object.keys(prev.lines).filter(function (k) { return !curr.lines[k]; });
      if (goneKeys.length > 0) {
        var gt = prev.lines[goneKeys[0]].title;
        speak('הוסר מהסל: ' + gt + '.');
        return;
      }
      // Existing line decremented
      for (var dk in curr.lines) {
        if (prev.lines[dk] && curr.lines[dk].quantity < prev.lines[dk].quantity) {
          speak('עודכנה כמות: ' + curr.lines[dk].title + ', ' + curr.lines[dk].quantity + '.');
          return;
        }
      }
    }

    // Same item_count but per-line quantity change (e.g. replaced)
    if (currCount === prevCount) {
      for (var sk in curr.lines) {
        if (prev.lines[sk] && curr.lines[sk].quantity !== prev.lines[sk].quantity) {
          speak('עודכנה כמות: ' + curr.lines[sk].title + ', ' + curr.lines[sk].quantity + '.');
          return;
        }
      }
    }
  }

  /* Home/category pass 2026-10-01 (H14): one add + one remove made this file
     read /cart.js 5 times (measured on live): each badge mutation batch polled,
     and every click inside the drawer polled twice more (400 ms and 900 ms).
     All triggers now go through ONE timer: a new trigger replaces the pending
     one, so a burst of triggers costs one read. A trigger that lands while a
     read is on the wire queues exactly one follow-up read, so the final cart
     state is still diffed and announced. */
  var pollInFlight = false;
  var pollAgain = false;
  var pollTimer = 0;
  function schedulePoll(ms) {
    clearTimeout(pollTimer);
    pollTimer = setTimeout(poll, ms);
  }
  function pollDone() {
    pollInFlight = false;
    if (pollAgain) {
      pollAgain = false;
      schedulePoll(120);
    }
  }
  function poll() {
    pollTimer = 0;
    if (pollInFlight) { pollAgain = true; return; }
    pollInFlight = true;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart) {
          var curr = snapshot(cart);
          if (lastSnapshot) announceDiff(lastSnapshot, curr);
          lastSnapshot = curr;
        }
        pollDone();
      })
      .catch(pollDone);
  }

  function init() {
    // Seed initial snapshot
    poll();

    // Whenever the cart badge count changes, the cart has changed — poll.
    var badges = document.querySelectorAll('[data-cart-count], [data-cart-total], [data-cart-items]');
    if (badges.length === 0) return;
    var mo = new MutationObserver(function () {
      // tiny defer so the cart API is definitely updated
      schedulePoll(120);
    });
    badges.forEach(function (b) {
      mo.observe(b, { characterData: true, childList: true, subtree: true, attributes: true, attributeFilter: ['data-cart-count', 'data-cart-total', 'data-cart-items'] });
    });

    // Also poll right after click events inside the cart drawer / cart page (qty, remove).
    document.addEventListener('click', function (e) {
      var inCart =
        e.target.closest('[data-js-site-cart-sidebar]') ||
        e.target.closest('#AjaxCartForm') ||
        e.target.closest('form[action*="/cart/add"]');
      if (!inCart) return;
      // One read once the change has had time to land. A badge mutation in the
      // meantime replaces this with its own 120 ms read (same timer).
      schedulePoll(900);
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
